import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Trash2, Eye, PlayCircle, Edit, Save, ExternalLink, MapPin, Bed, Bath, Wifi } from 'lucide-react';
import { ImageCarousel } from '@/components/ImageCarousel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_ADMIN = (() => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en', { month: 'short', year: 'numeric' });
    months.push({ value, label });
  }
  return months;
})();

const StatCard = ({ label, value, sub }) => (
  <div className="border border-gray-300 p-6 bg-cream">
    <div className="label-caps text-gray-600 mb-2">{label}</div>
    <div className="font-serif text-3xl md:text-4xl text-black">{value}</div>
    {sub && <div className="text-sm text-gray-500 mt-1">{sub}</div>}
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    approved: 'bg-black text-white',
    pending: 'bg-yellow-100 text-yellow-800 border border-yellow-300',
    rejected: 'bg-red-100 text-red-800 border border-red-300',
    active: 'bg-black text-white',
    new: 'bg-blue-100 text-blue-800 border border-blue-300',
    COMPLETE: 'bg-green-100 text-green-800 border border-green-300',
    free: 'bg-purple-100 text-purple-800 border border-purple-300',
  };
  return (
    <span className={`px-2 py-1 text-xs label-caps ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [leaseRequests, setLeaseRequests] = useState([]);
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [editingListing, setEditingListing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.post(
        `${API}/admin/users/${resettingUser.email}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${resettingUser.email}`);
      setResettingUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openEdit = (listing) => {
    setEditingListing(listing);
    setEditForm({
      title: listing.title,
      description: listing.description,
      monthly_price: listing.monthly_price,
      bedrooms: listing.bedrooms,
      bathrooms: listing.bathrooms,
      neighbourhood: listing.neighbourhood,
      approximate_address: listing.approximate_address,
      available_months: listing.available_months || [],
    });
  };

  const toggleEditMonth = (m) => {
    setEditForm((prev) => ({
      ...prev,
      available_months: prev.available_months.includes(m)
        ? prev.available_months.filter((x) => x !== m)
        : [...prev.available_months, m]
    }));
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/listings/${editingListing.id}`,
        {
          ...editForm,
          monthly_price: parseFloat(editForm.monthly_price),
          bedrooms: parseInt(editForm.bedrooms),
          bathrooms: parseInt(editForm.bathrooms),
        },
        { withCredentials: true }
      );
      toast.success('Listing updated');
      setEditingListing(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, listingsRes, usersRes, paymentsRes, enquiriesRes, leaseRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/listings`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/admin/enquiries`, { withCredentials: true }),
        axios.get(`${API}/admin/lease-requests`, { withCredentials: true }),
        axios.get(`${API}/admin/discount-codes`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setListings(listingsRes.data);
      setUsers(usersRes.data);
      setPayments(paymentsRes.data);
      setEnquiries(enquiriesRes.data);
      setLeaseRequests(leaseRes.data);
      setDiscountCodes(codesRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (listingId, status) => {
    try {
      await axios.patch(
        `${API}/admin/listings/${listingId}/verify?status=${status}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Listing ${status}`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await axios.delete(`${API}/admin/listings/${listingId}`, { withCredentials: true });
      toast.success('Listing deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="mb-12 pb-8 border-b border-gray-300">
          <div className="label-caps text-gray-600 mb-3">Admin Panel</div>
          <h1 className="font-serif text-4xl md:text-5xl mb-2" data-testid="admin-dashboard-title">
            Welcome, <em className="italic">{user?.name}</em>
          </h1>
          <p className="text-gray-600">Manage properties, users, payments, and platform settings</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <StatCard label="Total Listings" value={stats.listings.total} sub={`${stats.listings.pending} pending`} />
            <StatCard label="Approved" value={stats.listings.approved} />
            <StatCard label="Total Users" value={stats.users.total} sub={`${stats.users.hosts} hosts, ${stats.users.guests} guests`} />
            <StatCard label="Revenue" value={`R${stats.revenue.toLocaleString()}`} sub={`${stats.payments} payments`} />
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="listings" className="w-full">
          <TabsList className="mb-6 bg-transparent border-b border-gray-300 w-full justify-start rounded-none h-auto p-0">
            <TabsTrigger value="listings" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-listings">
              Listings ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-users">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-payments">
              Payments ({payments.length})
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-enquiries">
              Enquiries ({enquiries.length})
            </TabsTrigger>
            <TabsTrigger value="lease" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-lease">
              Lease ({leaseRequests.length})
            </TabsTrigger>
            <TabsTrigger value="codes" className="rounded-none label-caps data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-black data-[state=active]:shadow-none" data-testid="admin-tab-codes">
              Codes
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-300">
                <thead className="bg-cream-darker">
                  <tr className="text-left label-caps text-gray-700">
                    <th className="p-3">Property</th>
                    <th className="p-3">Host</th>
                    <th className="p-3">Neighbourhood</th>
                    <th className="p-3">Price</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => (
                    <tr key={listing.id} className="border-t border-gray-300 hover:bg-cream-darker" data-testid={`admin-listing-${listing.id}`}>
                      <td className="p-3 font-medium">{listing.title}</td>
                      <td className="p-3 text-gray-600">{listing.host_name}<br /><span className="text-xs">{listing.host_email}</span></td>
                      <td className="p-3">{listing.neighbourhood}</td>
                      <td className="p-3 font-serif">R{listing.monthly_price?.toLocaleString()}</td>
                      <td className="p-3"><StatusBadge status={listing.verification_status} /></td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedListing(listing)}
                            className="h-8 w-8 p-0"
                            data-testid={`admin-view-${listing.id}`}
                          >
                            <Eye className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEdit(listing)}
                            className="h-8 w-8 p-0"
                            data-testid={`admin-edit-${listing.id}`}
                          >
                            <Edit className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          {listing.verification_status !== 'approved' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVerify(listing.id, 'approved')}
                              className="h-8 w-8 p-0 text-green-700 hover:text-green-800"
                              data-testid={`admin-approve-${listing.id}`}
                            >
                              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          )}
                          {listing.verification_status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleVerify(listing.id, 'rejected')}
                              className="h-8 w-8 p-0 text-red-700 hover:text-red-800"
                              data-testid={`admin-reject-${listing.id}`}
                            >
                              <XCircle className="w-4 h-4" strokeWidth={1.5} />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(listing.id)}
                            className="h-8 w-8 p-0 text-red-700 hover:text-red-800"
                            data-testid={`admin-delete-${listing.id}`}
                          >
                            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-300">
                <thead className="bg-cream-darker">
                  <tr className="text-left label-caps text-gray-700">
                    <th className="p-3">Name</th>
                    <th className="p-3">Email</th>
                    <th className="p-3">Role</th>
                    <th className="p-3">Joined</th>
                    <th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.email} className="border-t border-gray-300 hover:bg-cream-darker">
                      <td className="p-3 font-medium">{u.name}</td>
                      <td className="p-3 text-gray-600">
                        <a href={`mailto:${u.email}`} className="hover:underline">{u.email}</a>
                      </td>
                      <td className="p-3"><StatusBadge status={u.role} /></td>
                      <td className="p-3 text-gray-600 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setResettingUser(u)}
                            className="text-xs caps hover:underline"
                            data-testid={`reset-password-${u.email}`}
                          >
                            Reset Password
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted italic font-serif mt-3">Passwords are stored as bcrypt hashes and cannot be viewed. Use "Reset Password" to set a new one if a user gets locked out.</p>
            </div>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-300">
                <thead className="bg-cream-darker">
                  <tr className="text-left label-caps text-gray-700">
                    <th className="p-3">Payment ID</th>
                    <th className="p-3">Amount</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Code</th>
                    <th className="p-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.m_payment_id} className="border-t border-gray-300 hover:bg-cream-darker">
                      <td className="p-3 font-mono text-xs">{p.m_payment_id.slice(0, 8)}...</td>
                      <td className="p-3 font-serif">R{p.amount}</td>
                      <td className="p-3"><StatusBadge status={p.status} /></td>
                      <td className="p-3 text-gray-600">{p.discount_code || '-'}</td>
                      <td className="p-3 text-gray-600 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            <div className="space-y-4">
              {enquiries.map((e) => (
                <div key={e.id} className="border border-gray-300 p-4 bg-cream">
                  <div className="flex justify-between mb-2">
                    <div className="font-medium">{e.guest_name} → {e.listing_title}</div>
                    <div className="text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">{e.message}</div>
                  <div className="text-xs text-gray-500">From: {e.guest_email}</div>
                </div>
              ))}
              {enquiries.length === 0 && <p className="text-center text-gray-500 py-8 font-serif italic">No enquiries yet</p>}
            </div>
          </TabsContent>

          {/* Lease Requests Tab */}
          <TabsContent value="lease">
            <div className="space-y-4">
              {leaseRequests.map((r) => (
                <div key={r.id} className="border border-gray-300 p-4 bg-cream">
                  <div className="flex justify-between mb-2">
                    <div className="font-medium">{r.guest_name} → {r.listing_title}</div>
                    <StatusBadge status={r.status} />
                  </div>
                  <div className="text-sm text-gray-700 mb-2">{r.guest_details}</div>
                  <div className="text-xs text-gray-500">Dates: {r.rental_start} to {r.rental_end}</div>
                </div>
              ))}
              {leaseRequests.length === 0 && <p className="text-center text-gray-500 py-8 font-serif italic">No lease requests yet</p>}
            </div>
          </TabsContent>

          {/* Discount Codes Tab */}
          <TabsContent value="codes">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-300">
                <thead className="bg-cream-darker">
                  <tr className="text-left label-caps text-gray-700">
                    <th className="p-3">Code</th>
                    <th className="p-3">Discount</th>
                    <th className="p-3">Uses</th>
                    <th className="p-3">Description</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {discountCodes.map((code) => (
                    <tr key={code.code} className="border-t border-gray-300 hover:bg-cream-darker">
                      <td className="p-3 font-mono font-bold">{code.code}</td>
                      <td className="p-3">{code.discount_value}% off</td>
                      <td className="p-3">{code.uses_count || 0} / {code.max_uses || '∞'}</td>
                      <td className="p-3 text-gray-600">{code.description}</td>
                      <td className="p-3">
                        <StatusBadge status={code.active ? 'active' : 'inactive'} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Reset Password Modal */}
      <Dialog open={!!resettingUser} onOpenChange={() => { setResettingUser(null); setNewPassword(''); }}>
        <DialogContent className="bg-paper border-gray-400 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Reset password
            </DialogTitle>
          </DialogHeader>
          {resettingUser && (
            <div className="space-y-4 pt-2">
              <div className="caps text-muted text-xs">For user</div>
              <div className="font-serif text-lg">{resettingUser.name} <span className="text-muted">({resettingUser.email})</span></div>
              <div>
                <Label className="caps text-ink mb-2 block text-xs">New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="new-password-input"
                />
                <p className="text-xs text-muted italic font-serif mt-2">
                  Share this new password with the user so they can log in and change it.
                </p>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !newPassword}
                  className="btn-editorial btn-solid"
                  data-testid="confirm-reset-password-btn"
                >
                  {resettingPassword ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  onClick={() => { setResettingUser(null); setNewPassword(''); }}
                  className="btn-editorial btn-ghost border-l"
                  style={{ borderLeft: '1px solid rgba(17,17,16,0.85)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Listing Modal */}
      <Dialog open={!!editingListing} onOpenChange={() => setEditingListing(null)}>
        <DialogContent className="max-w-3xl bg-paper border-gray-400 rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Edit Listing</DialogTitle>
          </DialogHeader>
          {editingListing && (
            <div className="space-y-4 pt-2">
              <div>
                <Label className="caps text-ink mb-1 block text-xs">Title</Label>
                <Input
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <Label className="caps text-ink mb-1 block text-xs">Description</Label>
                <Textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={4}
                  className="rounded-none border-gray-400 bg-paper"
                  data-testid="edit-description-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="caps text-ink mb-1 block text-xs">Price (R)</Label>
                  <Input
                    type="number"
                    value={editForm.monthly_price || ''}
                    onChange={(e) => setEditForm({ ...editForm, monthly_price: e.target.value })}
                    className="rounded-none border-gray-400 bg-paper"
                    data-testid="edit-price-input"
                  />
                </div>
                <div>
                  <Label className="caps text-ink mb-1 block text-xs">Bedrooms</Label>
                  <Input
                    type="number"
                    value={editForm.bedrooms || ''}
                    onChange={(e) => setEditForm({ ...editForm, bedrooms: e.target.value })}
                    className="rounded-none border-gray-400 bg-paper"
                  />
                </div>
                <div>
                  <Label className="caps text-ink mb-1 block text-xs">Bathrooms</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={editForm.bathrooms || ''}
                    onChange={(e) => setEditForm({ ...editForm, bathrooms: e.target.value })}
                    className="rounded-none border-gray-400 bg-paper"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="caps text-ink mb-1 block text-xs">Neighbourhood</Label>
                  <Input
                    value={editForm.neighbourhood || ''}
                    onChange={(e) => setEditForm({ ...editForm, neighbourhood: e.target.value })}
                    className="rounded-none border-gray-400 bg-paper"
                  />
                </div>
                <div>
                  <Label className="caps text-ink mb-1 block text-xs">Approximate Address</Label>
                  <Input
                    value={editForm.approximate_address || ''}
                    onChange={(e) => setEditForm({ ...editForm, approximate_address: e.target.value })}
                    className="rounded-none border-gray-400 bg-paper"
                  />
                </div>
              </div>
              <div>
                <Label className="caps text-ink mb-2 block text-xs">Available Months</Label>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {MONTHS_ADMIN.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => toggleEditMonth(m.value)}
                      className={`p-2 text-xs border transition-colors ${
                        editForm.available_months?.includes(m.value)
                          ? 'bg-ink text-paper border-ink'
                          : 'border-gray-400 hover:border-ink'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-4">
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="btn-editorial btn-solid"
                  data-testid="save-edit-btn"
                >
                  {saving ? 'Saving...' : 'Save Changes'} <Save className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => setEditingListing(null)}
                  className="btn-editorial btn-ghost border-l"
                  style={{ borderLeft: '1px solid rgba(17,17,16,0.85)' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Listing Details Modal — full review with photos before approval */}
      <Dialog open={!!selectedListing} onOpenChange={() => setSelectedListing(null)}>
        <DialogContent
          className="max-w-4xl bg-paper rounded-none border border-gray-400 p-0 overflow-hidden max-h-[92vh] flex flex-col"
          data-testid="admin-review-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-300 bg-paper">
            <DialogTitle className="font-serif text-2xl md:text-3xl">
              {selectedListing?.title}
            </DialogTitle>
            {selectedListing && (
              <div className="flex items-center gap-3 text-xs caps text-muted mt-2">
                <span
                  className={`px-2 py-0.5 border ${
                    selectedListing.verification_status === 'approved'
                      ? 'bg-ink text-paper border-ink'
                      : selectedListing.verification_status === 'rejected'
                      ? 'bg-red-700 text-white border-red-700'
                      : 'bg-paper text-ink border-gray-400'
                  }`}
                >
                  {selectedListing.verification_status || 'pending'}
                </span>
                <span>{selectedListing.neighbourhood}</span>
                <span>·</span>
                <span>R{selectedListing.monthly_price?.toLocaleString()}/mo</span>
              </div>
            )}
          </DialogHeader>
          {selectedListing && (
            <div className="flex-1 overflow-y-auto bg-paper" data-testid="admin-review-body">
              {/* Photo gallery */}
              <div className="w-full h-[280px] md:h-[380px] bg-gray-100 relative" data-testid="admin-review-carousel">
                {selectedListing.photos && selectedListing.photos.length > 0 ? (
                  <ImageCarousel photos={selectedListing.photos} alt={selectedListing.title} showCounter />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-muted">
                    <span className="caps text-[11px] mb-1">No photos uploaded</span>
                    <span className="text-xs font-serif italic">Host must upload at least 1 photo before submitting</span>
                  </div>
                )}
              </div>

              <div className="p-6 space-y-6">
                {/* Host + basic info */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="caps text-muted text-[10px] mb-1">Host</div>
                    <div className="text-ink" data-testid="admin-review-host-name">{selectedListing.host_name}</div>
                    <div className="text-muted text-xs">{selectedListing.host_email}</div>
                    {selectedListing.contact_whatsapp && <div className="text-muted text-xs">WhatsApp: {selectedListing.contact_whatsapp}</div>}
                    <div className="text-xs mt-1" data-testid="admin-review-promo-consent">
                      Promo consent: <strong>{selectedListing.promo_consent ? 'Yes' : 'No'}</strong>
                    </div>
                  </div>
                  <div>
                    <div className="caps text-muted text-[10px] mb-1">Type</div>
                    <div className="text-ink capitalize">
                      {selectedListing.property_type}
                      {selectedListing.place_type === 'private_room' ? ' · Room' : ''}
                    </div>
                  </div>
                  <div>
                    <div className="caps text-muted text-[10px] mb-1">Size</div>
                    <div className="text-ink flex items-center gap-2">
                      <Bed className="w-3.5 h-3.5" strokeWidth={1.5} /> {selectedListing.bedrooms}
                      <Bath className="w-3.5 h-3.5 ml-2" strokeWidth={1.5} /> {selectedListing.bathrooms}
                    </div>
                  </div>
                  <div>
                    <div className="caps text-muted text-[10px] mb-1">Wi-Fi</div>
                    <div className="text-ink flex items-center gap-1">
                      <Wifi className="w-3.5 h-3.5" strokeWidth={1.5} /> {selectedListing.wifi_speed ? `${selectedListing.wifi_speed} Mbps` : '—'}
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <div className="caps text-muted text-[10px] mb-1">Location</div>
                  <div className="text-ink flex items-start gap-2 text-sm" data-testid="admin-review-address">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                    <div>
                      <div>{selectedListing.neighbourhood}, Cape Town</div>
                      {selectedListing.approximate_address && (
                        <div className="text-muted text-xs mt-0.5">{selectedListing.approximate_address}</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <div className="caps text-muted text-[10px] mb-2">Description</div>
                  <p className="text-ink text-sm leading-relaxed whitespace-pre-wrap" data-testid="admin-review-description">
                    {selectedListing.description}
                  </p>
                </div>

                {/* Monthly prices */}
                {selectedListing.available_months && selectedListing.available_months.length > 0 && (
                  <div>
                    <div className="caps text-muted text-[10px] mb-2">Availability &amp; monthly pricing</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      {[...selectedListing.available_months].sort().map((m) => {
                        const price = (selectedListing.monthly_prices || {})[m];
                        const label = new Date(m + '-01').toLocaleDateString('en', { month: 'short', year: 'numeric' });
                        return (
                          <div key={m} className="flex justify-between border-b border-line pb-1" data-testid={`admin-review-price-${m}`}>
                            <span>{label}:</span>
                            <span className="font-serif font-semibold text-ink">
                              R{Number(price ?? selectedListing.monthly_price).toLocaleString('en-ZA').replace(/,/g, ' ')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Amenities */}
                <div>
                  <div className="caps text-muted text-[10px] mb-2">Amenities</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-sm">
                    {[
                      ['workspace', 'Workspace'], ['air_conditioning', 'Air conditioning'], ['heating', 'Heating'],
                      ['kitchen', 'Kitchen'], ['laundry', 'Laundry'], ['parking', 'Parking'],
                      ['pool', 'Pool'], ['gym', 'Gym'], ['balcony', 'Balcony'],
                      ['security', 'Security'], ['pet_friendly', 'Pet friendly'], ['smart_tv', 'Smart TV'],
                      ['tv', 'TV'], ['sea_view', 'Sea view'], ['mountain_view', 'Mountain view'],
                    ].filter(([k]) => selectedListing[k]).map(([k, label]) => (
                      <div key={k} className="flex items-center gap-1.5 text-ink">
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> {label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Verification video */}
                {selectedListing.verification_video && (
                  <div className="border border-gray-300 p-3 flex items-center gap-2 bg-paper">
                    <PlayCircle className="w-5 h-5" strokeWidth={1.5} />
                    <span className="text-sm">Verification video uploaded</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {selectedListing && (
            <div className="border-t border-gray-300 bg-paper p-4 flex flex-wrap gap-2 justify-between items-center flex-shrink-0">
              <a
                href={`/listings/${selectedListing.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs caps text-ink hover:underline flex items-center gap-1"
                data-testid="admin-open-full-listing"
              >
                <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} /> Open full listing
              </a>
              <div className="flex gap-2">
                {selectedListing.verification_status !== 'rejected' && (
                  <Button
                    onClick={() => { handleVerify(selectedListing.id, 'rejected'); setSelectedListing(null); }}
                    variant="outline"
                    className="border-red-700 text-red-700 hover:bg-red-700 hover:text-white rounded-none caps bg-paper text-xs"
                    data-testid="admin-modal-reject-btn"
                  >
                    Reject
                  </Button>
                )}
                {selectedListing.verification_status !== 'approved' && (
                  <Button
                    onClick={() => { handleVerify(selectedListing.id, 'approved'); setSelectedListing(null); }}
                    className="bg-ink hover:bg-black text-paper rounded-none caps text-xs"
                    data-testid="admin-modal-approve-btn"
                  >
                    Approve
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
